import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import {
  CMR_WRITE_MODE_LABELS,
  VALID_CMR_WRITE_MODES,
  formatCmrFieldLabel,
  listCmrFields,
} from "constants/cmrFieldRegistry";
import { requestCmrFieldAddition } from "services/fde/cmrDataFlowService";

/**
 * Registry-constrained CMR field multi-select + optional write-mode for outbound.
 * Never allows free-text field ids.
 */
function CmrFieldPicker({
  value = [],
  onChange,
  mode = "inbound",
  tciId = "",
  disabled = false,
  showRequestNewField = true,
}) {
  const fields = useMemo(() => listCmrFields(), []);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { requestedFieldLabel: "", notes: "" },
  });

  const selectedIds = useMemo(
    () => new Set((value || []).map((row) => row.field).filter(Boolean)),
    [value],
  );

  const toggleField = (fieldId) => {
    if (disabled) return;
    const exists = selectedIds.has(fieldId);
    if (exists) {
      onChange((value || []).filter((row) => row.field !== fieldId));
      return;
    }
    const next = {
      field: fieldId,
      ...(mode === "inbound"
        ? { label: formatCmrFieldLabel(fieldId) }
        : { writeMode: "confirmRequired-by-patient" }),
    };
    onChange([...(value || []), next]);
  };

  const setWriteMode = (fieldId, writeMode) => {
    onChange(
      (value || []).map((row) =>
        row.field === fieldId ? { ...row, writeMode } : row,
      ),
    );
  };

  const onRequestSubmit = async (formValues) => {
    setRequesting(true);
    setRequestError(null);
    setRequestSuccess(null);
    try {
      await requestCmrFieldAddition({
        requestedFieldLabel: formValues.requestedFieldLabel,
        notes: formValues.notes,
        tciId: tciId || null,
      });
      setRequestSuccess("Request sent to FusionMSI support.");
      reset({ requestedFieldLabel: "", notes: "" });
    } catch (err) {
      setRequestError(err?.message || "Failed to send field request.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {mode === "inbound" ? "Inbound CMR fields" : "Outbound CMR fields"}
      </Typography>
      <FormGroup>
        {fields.map((field) => {
          const checked = selectedIds.has(field.id);
          const outboundRow = (value || []).find((r) => r.field === field.id);
          return (
            <Box key={field.id} sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => toggleField(field.id)}
                    disabled={disabled}
                  />
                }
                label={field.label}
              />
              {mode === "outbound" && checked ? (
                <FormControl size="small" sx={{ ml: 4, minWidth: 240 }} disabled={disabled}>
                  <InputLabel id={`wm-${field.id}`}>Write mode</InputLabel>
                  <Select
                    labelId={`wm-${field.id}`}
                    label="Write mode"
                    value={outboundRow?.writeMode || "confirmRequired-by-patient"}
                    onChange={(e) => setWriteMode(field.id, e.target.value)}
                  >
                    {VALID_CMR_WRITE_MODES.map((wm) => (
                      <MenuItem key={wm} value={wm}>
                        {CMR_WRITE_MODE_LABELS[wm] || wm}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          );
        })}
      </FormGroup>

      {showRequestNewField ? (
        <LoadingButton
          size="small"
          sx={{ mt: 1 }}
          disabled={disabled}
          onClick={() => {
            setRequestOpen(true);
            setRequestError(null);
            setRequestSuccess(null);
          }}
        >
          Request new CMR field
        </LoadingButton>
      ) : null}

      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request a new CMR field</DialogTitle>
        <DialogContent>
          {requestError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRequestError(null)}>
              {requestError}
            </Alert>
          ) : null}
          {requestSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRequestSuccess(null)}>
              {requestSuccess}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends a request to support@fusionmsi.com. Field names are not free-text in the data-flow
            config — new fields must be added to the registry after review.
          </Typography>
          <Controller
            name="requestedFieldLabel"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Requested field name"
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
          />
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Notes (optional)" fullWidth multiline minRows={2} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setRequestOpen(false)}>Close</LoadingButton>
          <LoadingButton
            variant="contained"
            loading={requesting}
            onClick={handleSubmit(onRequestSubmit)}
          >
            Send request
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

CmrFieldPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["inbound", "outbound"]),
  tciId: PropTypes.string,
  disabled: PropTypes.bool,
  showRequestNewField: PropTypes.bool,
};

export default CmrFieldPicker;
